import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DfTabContent, DfTabHeader, DfTabs } from '@danfoss/mosaic-react';
import { Fragment } from 'react/jsx-runtime';
import { ActiveTabChangeEventArgsType, DfTabsCustomEvent } from '@danfoss/mosaic-core';
import styles from './Tabs.module.scss';

type Props = {
    className?: string;
    isSticky?: boolean;
    isContentAnchored?: boolean;
    activeTabId?: string;
    isDisabled?: boolean;
    tabs: {
        id: string;
        tab: string;
        content: React.ReactNode;
    }[];
    variant?: 'solid' | 'light';
    onActiveTabChange?: (tabId: string) => void;
};

const Tabs: React.FC<Props> = ({ isDisabled, activeTabId, className, isContentAnchored, isSticky, tabs, variant = 'light', onActiveTabChange }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { !!tabs.length && setMounted(true); }, [tabs]);

    const { activeTab, handleActiveTabChange } = useActiveTabStateOverride({
        isDisabled,
        activeTabId,
        onActiveTabChange
    });

    if (!mounted) return null;

    return (
        <DfTabs
            contentClassName={styles.content}
            searchParamsHidden
            activeTabId={activeTab}
            className={className}
            variant={variant}
            isCustomLinkNavigation={true} // Controls whether we use built in history pushState
            isSticky={isSticky}
            isContentAnchored={isContentAnchored}
            onActiveTabChange={handleActiveTabChange}
        >
            {tabs.map(({ id, tab, content }, index) => (
                <Fragment key={index}>
                    {tab && <DfTabHeader
                        slot="header"
                        tabId={id}
                        aria-disabled={isDisabled}
                    >
                        {tab}
                    </DfTabHeader>}
                    <DfTabContent
                        slot='content'
                        tabId={id}
                    >
                        {content}
                    </DfTabContent>
                </Fragment>
            ))}
        </DfTabs>
    );
};

export default Tabs;


// This is needed because mosaic <DfTabs> component for some reason has an internal state which doesn't honor external state and onClick handlers ...
const useActiveTabStateOverride = ({ isDisabled, activeTabId, onActiveTabChange }: Pick<Props, 'isDisabled' | 'activeTabId' | 'onActiveTabChange'>) => {
    const [activeTab, setActiveTab] = useState(activeTabId);

    const timeoutRef = useRef<NodeJS.Timeout>();
    const currentActiveTabRef = useRef(activeTab);
    const previousActiveTabRef = useRef(activeTab);

    useEffect(() => {
        previousActiveTabRef.current = activeTabId;
        setActiveTab(activeTabId);
    }, [activeTabId]);

    const checkActiveTabReset = useCallback(() => {
        if (currentActiveTabRef.current === previousActiveTabRef.current) return;
        setActiveTab("");
        setTimeout(() => setActiveTab(previousActiveTabRef.current), 25);
    }, []);

    const handleActiveTabChange = useCallback((e: DfTabsCustomEvent<ActiveTabChangeEventArgsType>) => {
        timeoutRef.current && clearTimeout(timeoutRef.current);
        const tabId = e.detail.tabId;
        if (isDisabled) return;

        onActiveTabChange?.(tabId);
        currentActiveTabRef.current = tabId;

        timeoutRef.current = setTimeout(checkActiveTabReset, 100);
    }, [isDisabled, onActiveTabChange, checkActiveTabReset]);

    return {
        activeTab,
        handleActiveTabChange
    };
};